import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Beaker as BeakerIcon } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Preparing Sulfuric Acid',
    content: 'Sulfuric acid (H₂SO₄) is a strong diprotic acid that dissociates in two steps:\n\nH₂SO₄ -> H+ + HSO4- (complete)\nHSO4- <-> H+ + SO4^2- (partial)\n\nWe use 15 mL of 6 M H₂SO₄ to dissolve the copper(II) oxide.',
    whatHappens: 'The sulfuric acid solution is measured carefully. This acid will react with the basic CuO to form a soluble salt.',
    observe: [
      'Clear, colorless H₂SO₄ solution',
      'Measure approximately 15 mL',
      'CAUTION: Highly corrosive acid',
      'Never add water to acid - add acid to water'
    ]
  },
  2: {
    title: 'Acid-Base Reaction: CuO + H₂SO₄',
    content: 'This is an acid-base (neutralization) reaction:\n\nCuO(s) + H₂SO₄(aq) -> CuSO₄(aq) + H₂O(l)\n\nCopper(II) oxide is a basic oxide that reacts with the acid to form copper(II) sulfate, a soluble salt.',
    whatHappens: 'The black CuO dissolves in the sulfuric acid. The solution turns from colorless to bright blue as CuSO₄ forms. The black solid gradually disappears.',
    observe: [
      'Black CuO solid dissolves',
      'Solution changes from colorless to blue',
      'Color intensifies as more CuO dissolves',
      'Final solution is bright blue CuSO₄',
      'Reaction may be slightly exothermic'
    ]
  }
};

export default function Part4Dissolution2() {
  const [acidPrepared, setAcidPrepared] = useState(false);
  const [acidAdding, setAcidAdding] = useState(false);
  const [dissolutionProgress, setDissolutionProgress] = useState(0);
  const [dissolutionComplete, setDissolutionComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [acidPrepared, dissolutionComplete].filter(Boolean).length;

  const handlePrepareAcid = useCallback(() => {
    if (!acidPrepared) {
      setAcidPrepared(true);
      setCurrentStep(2);
    }
  }, [acidPrepared]);

  const handleAddAcid = useCallback(() => {
    if (acidPrepared && !acidAdding && !dissolutionComplete) {
      setAcidAdding(true);

      // Animate dissolution
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.05;
        setDissolutionProgress(progress);
        if (progress >= 1) {
          clearInterval(interval);
          setAcidAdding(false);
          setDissolutionComplete(true);
        }
      }, 150);
    }
  }, [acidPrepared, acidAdding, dissolutionComplete]);

  // Color transition from black CuO to blue CuSO₄
  const getSolutionColor = () => {
    if (dissolutionComplete) return copperCycleColors.cuSO4Solution;
    if (dissolutionProgress > 0.66) return copperCycleColors.cuO_H2SO4_Mid2;
    if (dissolutionProgress > 0.33) return copperCycleColors.cuO_H2SO4_Mid1;
    if (dissolutionProgress > 0) return copperCycleColors.cuOSolid;
    return 'transparent';
  };

  const getSolidOpacity = () => {
    return Math.max(0, 1 - dissolutionProgress * 1.2);
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part VII: CuO + H₂SO₄</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare H₂SO₄', hint: 'Measure 15 mL of 6 M H₂SO₄', done: acidPrepared },
            { id: 2, title: 'Add Acid', hint: 'Watch black dissolve to blue', done: dissolutionComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Color transition indicator */}
        <div className="color-transition">
          <span className="label">Dissolution Progress:</span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${dissolutionProgress * 100}%`,
                background: `linear-gradient(90deg, ${copperCycleColors.cuOSolid}, ${copperCycleColors.cuO_H2SO4_Mid1}, ${copperCycleColors.cuO_H2SO4_Mid2}, ${copperCycleColors.cuSO4Solution})`
              }}
            />
          </div>
          <div className="color-endpoints">
            <span style={{ color: copperCycleColors.cuOSolid }}>Black CuO</span>
            <span style={{ color: copperCycleColors.cuSO4Solution }}>Blue CuSO₄</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Dissolution of CuO in H₂SO₄"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['Beaker with CuO', '6 M H₂SO₄', 'Stirring rod']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* H₂SO₄ Bottle */}
            <div
              className={`equipment-station ${currentStep === 1 && !acidPrepared ? 'clickable' : ''}`}
              onClick={handlePrepareAcid}
              style={{ cursor: currentStep === 1 && !acidPrepared ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 50 80" width={50} height={80}>
                <rect x="12" y="25" width="26" height="45" fill="#FFFFFF" stroke="#888" strokeWidth="1" rx="2" />
                <rect x="17" y="15" width="16" height="12" fill="#AA4444" rx="2" />
                <text x="25" y="48" fontSize="6" fill="#333" textAnchor="middle">H₂SO₄</text>
                <text x="25" y="56" fontSize="5" fill="#666" textAnchor="middle">6 M</text>
                {/* Warning symbol */}
                <polygon points="25,62 20,70 30,70" fill="#FFD700" stroke="#333" strokeWidth="0.5" />
                <text x="25" y="68" fontSize="4" fill="#333" textAnchor="middle">!</text>
                <text x="25" y="78" fontSize="7" fill="#E0E0E0" textAnchor="middle">Acid</text>
              </svg>
              <span className="equipment-label">15 mL H₂SO₄</span>
            </div>

            {/* Reaction Beaker with CuO */}
            <div
              className={`equipment-station ${currentStep === 2 && !dissolutionComplete ? 'clickable' : ''}`}
              onClick={handleAddAcid}
              style={{ cursor: currentStep === 2 && acidPrepared && !dissolutionComplete ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 160 200" width={180} height={220}>
                <defs>
                  <linearGradient id={`beaker-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                  <linearGradient id={`solution-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={getSolutionColor()} stopOpacity="0.85" />
                    <stop offset="100%" stopColor={getSolutionColor()} stopOpacity="0.95" />
                  </linearGradient>
                </defs>

                {/* Beaker */}
                <rect x="30" y="30" width="100" height="130" fill={`url(#beaker-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="2" />

                {/* Solution rising as acid is added */}
                {dissolutionProgress > 0 && (
                  <rect
                    x="33"
                    y={155 - dissolutionProgress * 80}
                    width="94"
                    height={dissolutionProgress * 80 + 5}
                    fill={`url(#solution-${id})`}
                  >
                    <animate
                      attributeName="fill"
                      values={acidAdding ?
                        `${copperCycleColors.cuO_H2SO4_Mid1};${copperCycleColors.cuO_H2SO4_Mid2};${copperCycleColors.cuSO4Solution}` : undefined}
                      dur={acidAdding ? "3s" : undefined}
                      fill="freeze"
                    />
                  </rect>
                )}

                {/* Black CuO solid (disappearing) */}
                <g opacity={getSolidOpacity()}>
                  <ellipse cx="80" cy="155" rx="40" ry="8" fill={copperCycleColors.cuOSolid} />
                  <ellipse cx="80" cy="152" rx="36" ry="6" fill="#2A2A2A" />
                  {/* Boiling chips visible */}
                  <circle cx="60" cy="150" r="2" fill={copperCycleColors.boilingChips} />
                  <circle cx="95" cy="152" r="2" fill={copperCycleColors.boilingChips} />
                </g>

                {/* Dissolution effect - particles rising and dissolving */}
                {acidAdding && (
                  <g>
                    {[...Array(8)].map((_, i) => (
                      <circle
                        key={i}
                        cx={50 + i * 10}
                        cy={150}
                        r={2}
                        fill={copperCycleColors.cuOSolid}
                      >
                        <animate
                          attributeName="cy"
                          values="150;100;150"
                          dur={`${1 + i * 0.15}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="fill"
                          values={`${copperCycleColors.cuOSolid};${copperCycleColors.cuO_H2SO4_Mid2};${copperCycleColors.cuSO4Solution}`}
                          dur={`${1 + i * 0.15}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="1;0.5;0"
                          dur={`${1 + i * 0.15}s`}
                          repeatCount="indefinite"
                        />
                      </circle>
                    ))}
                  </g>
                )}

                {/* Stirring rod */}
                {acidAdding && (
                  <g>
                    <line x1="80" y1="25" x2="80" y2="120" stroke="#777" strokeWidth="4" strokeLinecap="round">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        values="0 80 120;15 80 120;-15 80 120;0 80 120"
                        dur="0.6s"
                        repeatCount="indefinite"
                      />
                    </line>
                  </g>
                )}

                <text x="80" y="180" fontSize="10" fill="#E0E0E0" textAnchor="middle">Reaction Beaker</text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !acidPrepared && (
              <div className="status-message info">
                <BeakerIcon size={20} />
                Measure 15 mL of 6 M H₂SO₄ (caution: corrosive!)
              </div>
            )}
            {currentStep === 2 && acidPrepared && !acidAdding && !dissolutionComplete && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to add H₂SO₄ slowly - watch the black solid dissolve!
              </div>
            )}
            {acidAdding && (
              <div className="status-message reaction">
                <FlaskConical size={20} />
                CuO dissolving... Black → Indigo → Blue transition
              </div>
            )}
            {dissolutionComplete && (
              <div className="status-message success">
                <Check size={20} />
                Bright blue CuSO₄ solution formed!
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Acid-Base Reaction</h4>
            <div className="equation">
              CuO + H₂SO₄ → CuSO₄ + H₂O
            </div>
            <p>
              The black copper(II) oxide reacts with sulfuric acid to form copper(II) sulfate.
              The beautiful blue color is characteristic of hydrated Cu²⁺ ions in solution.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 60px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
            text-align: center;
          }

          .color-transition {
            margin-top: 12px;
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .color-transition .label {
            font-size: 11px;
            color: var(--text-secondary);
            display: block;
            margin-bottom: 8px;
          }

          .progress-bar {
            height: 12px;
            background: #222;
            border-radius: 6px;
            overflow: hidden;
            margin-bottom: 6px;
          }

          .progress-fill {
            height: 100%;
            transition: width 0.15s;
            border-radius: 6px;
          }

          .color-endpoints {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.reaction {
            background: rgba(75, 0, 130, 0.3);
            color: #9370DB;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: ${copperCycleColors.cuSO4Solution};
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
